import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { withNext } from "@/lib/auth/safeNext";

/**
 * Gates a subtree on `authenticated`. While loading, renders a placeholder.
 * On anonymous, redirects to /login carrying the intended URL via `?next=`
 * AND `location.state.from` (belt + braces) so the login page can return
 * users to where they were trying to go — and it does: the auth forms read
 * `next` through `safeNext` (billing-comms Teil 2a).
 */
export function PrivateRoute({ children }: { children?: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.kind === "loading") {
    return <InlineLoadingSpinner />;
  }

  if (state.kind === "anonymous") {
    // The user asked to leave (explicit logout): the login page they land on
    // must not offer to bring them — or the next person at this browser — back
    // here (billing-comms Teil 2a). An expired session still carries `next`.
    if (state.loggedOut) return <Navigate to="/auth/login" replace />;
    const next = `${location.pathname}${location.search}${location.hash}`;
    const target = next && next !== "/" ? `/auth/login?next=${encodeURIComponent(next)}` : "/auth/login";
    return <Navigate to={target} replace state={{ from: next }} />;
  }

  if (state.kind === "mfa-required") {
    // Mid-MFA on a private URL: it rides along so the verify page can return
    // there (validated by `withNext`; "/" is not carried).
    return (
      <Navigate
        to={withNext("/auth/mfa-verify", `${location.pathname}${location.search}${location.hash}`)}
        replace
      />
    );
  }

  return children ? <>{children}</> : <Outlet />;
}
